/**
 * Platform Builder - constructs PlatformProperties from HeadlessConfig
 *
 * This module reads the raw headless metafield config directly using the
 * HeadlessConfig accessor class, without any transformation.
 */

import type { PlatformProperties } from '../types/window-analyzify';
import type { ProductIdFormat } from '../types/platforms';
import type { HeadlessConfig } from './headless-config';

/**
 * Build PlatformProperties object from HeadlessConfig
 *
 * @param config - HeadlessConfig accessor instance
 * @returns PlatformProperties object matching CDN script expectations
 */
export function buildPlatformProperties(config: HeadlessConfig): PlatformProperties {
  return {
    GTM: buildGTMProperties(config),
    GADS: buildGADSProperties(config),
    GA4: buildGA4Properties(config),
    X: buildXProperties(config),
    BING: buildBingProperties(config),
    FACEBOOK: buildFacebookProperties(config),
    SERVERSIDE: buildServerSideProperties(config),
    PINTEREST: buildPinterestProperties(config),
    CRITEO: buildCriteoProperties(config),
    CLARITY: buildClarityProperties(config),
    HOTJAR: buildHotjarProperties(config),
    SNAPCHAT: buildSnapchatProperties(config),
    KLAVIYO: buildKlaviyoProperties(config),
    TIKTOK: buildTiktokProperties(config),
    // CDN click handlers read these statuses even though the SDK does not
    // load or initialize either platform yet.
    REDDIT: { status: false },
    OPENAI: { status: false },
  };
}

// ═══════════════════════════════════════════════════════════════
// GTM
// ═══════════════════════════════════════════════════════════════

function buildGTMProperties(config: HeadlessConfig) {
  return {
    status: config.bool('gtm_dataLayer'),
    id: config.str('gtm_id'),
    multiple_view_item_list: config.bool('multiple_view_item_list'),
    feed_country: config.str('gtm_feed_country') || 'US',
    feed_region: config.str('gtm_feed_region') || config.str('gtm_feed_country') || 'US',
    feed_language: config.str('gtm_feed_language') || 'en',
    merchant_id: config.str('gtm_merchant_id') || '',
    enhanced_params: true,
    replace_session_id: config.bool('gtm_replace_session_id'),
    replace_gclid: config.bool('gtm_replace_gclid'),
    variant_changed: config.has('gtm_variant_changed')
      ? config.bool('gtm_variant_changed')
      : true, // Default true
    generate_lead: config.has('gtm_forms') ? config.bool('gtm_forms') : true,
  };
}

// ═══════════════════════════════════════════════════════════════
// GOOGLE ADS
// ═══════════════════════════════════════════════════════════════

function buildGADSProperties(config: HeadlessConfig) {
  const feedCountry = config.str('feed_country') || 'US';
  const feedRegion = config.str('feed_region') || 'US';
  const feedLanguage = config.str('feed_language') || 'en';
  const merchantId = config.str('merchant_id') || '';
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('gads_status') || config.bool('gads_status', true) || config.bool('gads_remarketing') || config.bool('gads_remarketing', true),
    // the Analyzify app configuration defaults: conversion linker and ads data
    // redaction are on unless the setting is explicitly false
    conversion_linker: config.has('gads_conversion_linker') ? config.bool('gads_conversion_linker') : true,
    allow_ad_personalization_signals: config.bool('gads_allow_ad_personalization_signals') || config.bool('gads_remarketing'),
    ads_data_redaction: config.has('gads_ads_data_redaction') ? config.bool('gads_ads_data_redaction') : true,
    url_passthrough: config.bool('gads_url_passthrough'),
    primary: {
      status: config.bool('gads_status'),
      id: config.str('gads_id'),
      feed_country: config.str('gads_feed_country') || feedCountry,
      feed_region: config.str('gads_feed_region') || feedRegion,
      merchant_id: config.str('gads_merchant_id') || merchantId,
      feed_language: config.str('gads_feed_language') || feedLanguage,
      product_id_format: (config.str('gads_product_id_format') || 'product_sku') as ProductIdFormat,
      is_cogs_active: config.bool('is_cogs_active'),
      replace_session_id: config.bool('gads_replace_session_id'),
      replace_gclid: config.bool('gads_replace_gclid'),
      remarketing: {
        status: config.bool('gads_remarketing'),
        gads_remarketing_id_format: (config.str('gads_remarketing_id_format') || 'product_sku') as ProductIdFormat,
        events: {
          view_item_list: config.bool('gads_remarketing') && config.bool('gads_rem_view_item_list'),
          view_item: config.bool('gads_remarketing') && config.bool('gads_rem_view_item'),
          add_to_cart: config.bool('gads_remarketing') && config.bool('gads_rem_atc'),
          remove_from_cart: config.bool('gads_remarketing') && config.bool('gads_rem_rfc'),
          begin_checkout: config.bool('gads_remarketing') && config.bool('gads_rem_bc'),
          purchase: config.bool('gads_remarketing') && config.bool('gads_rem_purchase'),
          generate_lead: config.bool('gads_remarketing') && config.bool('gads_rem_forms'),
        },
      },
      conversions: {
        view_item: {
          status: config.bool('gads_c_view_item_status'),
          value: config.str('gads_c_view_item'),
        },
        add_to_cart: {
          status: config.bool('gads_c_add_to_cart_status'),
          value: config.str('gads_c_add_to_cart'),
        },
        begin_checkout: {
          status: config.bool('gads_c_begin_checkout_status'),
          value: config.str('gads_c_begin_checkout'),
        },
        purchase: {
          status: config.bool('gads_c_purchase_status'),
          value: config.str('gads_c_purchase'),
        },
        generate_lead: {
          status: config.bool('gads_c_all_forms_status'),
          value: config.str('gads_c_all_forms'),
        },
      },
    },
    secondary: {
      status: config.bool('gads_status', true),
      id: config.str('gads_id', true),
      feed_country: config.str('gads_feed_country', true) || '',
      feed_region: config.str('gads_feed_region', true) || '',
      merchant_id: config.str('gads_merchant_id', true) || '',
      feed_language: config.str('gads_feed_language', true) || '',
      product_id_format: (config.str('gads_product_id_format', true) || 'product_sku') as ProductIdFormat,
      is_cogs_active: config.bool('is_cogs_active', true),
      remarketing: {
        status: config.bool('gads_remarketing', true),
        gads_remarketing_id_format: (config.str('gads_remarketing_id_format', true) || 'product_sku') as ProductIdFormat,
        events: {
          view_item_list: config.bool('gads_remarketing', true) && config.bool('gads_rem_view_item_list', true),
          view_item: config.bool('gads_remarketing', true) && config.bool('gads_rem_view_item', true),
          add_to_cart: config.bool('gads_remarketing', true) && config.bool('gads_rem_atc', true),
          remove_from_cart: config.bool('gads_remarketing', true) && config.bool('gads_rem_rfc', true),
          begin_checkout: config.bool('gads_remarketing', true) && config.bool('gads_rem_bc', true),
          purchase: config.bool('gads_remarketing', true) && config.bool('gads_rem_purchase', true),
          generate_lead: config.bool('gads_remarketing', true) && config.bool('gads_rem_forms', true),
        },
      },
      conversions: {
        view_item: {
          status: config.bool('gads_c_view_item_status', true),
          value: config.str('gads_c_view_item', true),
        },
        add_to_cart: {
          status: config.bool('gads_c_add_to_cart_status', true),
          value: config.str('gads_c_add_to_cart', true),
        },
        begin_checkout: {
          status: config.bool('gads_c_begin_checkout_status', true),
          value: config.str('gads_c_begin_checkout', true),
        },
        purchase: {
          status: config.bool('gads_c_purchase_status', true),
          value: config.str('gads_c_purchase', true),
        },
        generate_lead: {
          status: config.bool('gads_c_all_forms_status', true),
          value: config.str('gads_c_all_forms', true),
        },
      },
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// GA4
// ═══════════════════════════════════════════════════════════════

function buildGA4Properties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('ga4_status') || config.bool('ga4_status', true),
    multiple_view_item_list: config.bool('multiple_view_item_list'),
    enhanced_params: true,
    all_forms: config.bool('ga4_all_forms'),
    primary: {
      status: config.bool('ga4_status'),
      id: config.str('ga4_id'),
      product_id_format: (config.str('ga4_id_format') || 'product_sku') as ProductIdFormat,
      replace_session_id: config.bool('ga4_replace_session_id'),
      events: {
        variant_changed: config.has('ga4_variant_changed')
          ? config.bool('ga4_variant_changed')
          : true, // Default true
        view_item_list: config.bool('ga4_view_item_list'),
        select_item: config.bool('ga4_select_item'),
        nav_click: config.bool('ga4_navigation_button_click'),
        view_item: config.bool('ga4_view_item'),
        add_to_cart: config.bool('ga4_atc'),
        add_to_wishlist: config.bool('ga4_atw'),
        view_cart: config.bool('ga4_view_cart'),
        remove_from_cart: config.bool('ga4_rfc'),
        begin_checkout: config.bool('ga4_bc'),
        purchase: config.bool('ga4_purchase'),
        generate_lead: config.bool('ga4_all_forms'),
        page_404: config.has('ga4_view_404') ? config.bool('ga4_view_404') : true,
        search: config.bool('ga4_events_sch'),
      },
    },
    secondary: {
      status: config.bool('ga4_status', true),
      id: config.str('ga4_id', true),
      product_id_format: (config.str('ga4_id_format', true) || 'product_sku') as ProductIdFormat,
      replace_session_id: config.bool('ga4_replace_session_id', true),
      events: {
        variant_changed: config.has('ga4_variant_changed', true)
          ? config.bool('ga4_variant_changed', true)
          : true, // Default true
        view_item_list: config.bool('ga4_view_item_list', true),
        select_item: config.bool('ga4_select_item', true),
        nav_click: config.bool('ga4_navigation_button_click', true),
        view_item: config.bool('ga4_view_item', true),
        add_to_cart: config.bool('ga4_atc', true),
        add_to_wishlist: config.bool('ga4_atw', true),
        view_cart: config.bool('ga4_view_cart', true),
        remove_from_cart: config.bool('ga4_rfc', true),
        begin_checkout: config.bool('ga4_bc', true),
        purchase: config.bool('ga4_purchase', true),
        generate_lead: config.bool('ga4_all_forms', true),
        page_404: config.has('ga4_view_404', true) ? config.bool('ga4_view_404', true) : true,
        search: config.bool('ga4_events_sch', true),
      },
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// X (TWITTER)
// ═══════════════════════════════════════════════════════════════

function buildXProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('twitter_status'),
    id: config.str('twitter_id'),
    product_id_format: (config.str('twitter_id_format') || 'product_sku') as ProductIdFormat,
    events: {
      page_view: config.str('twitter_pv_id'),
      add_to_cart: config.str('twitter_atc_id'),
      begin_checkout: config.str('twitter_bc_id'),
      search: config.str('twitter_search_id'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// BING
// ═══════════════════════════════════════════════════════════════

function buildBingProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('bing_status'),
    id: config.str('bing_id'),
    product_id_format: (config.str('bing_id_format') || 'product_sku') as ProductIdFormat,
    events: {
      home: config.has('bing_home') ? config.bool('bing_home') : true,
      view_item: config.has('bing_vi') ? config.bool('bing_vi') : true,
      view_item_list: config.has('bing_vil') ? config.bool('bing_vil') : true,
      view_cart: config.has('bing_vc') ? config.bool('bing_vc') : true,
      search: config.has('bing_search') ? config.bool('bing_search') : true,
      add_to_cart: config.bool('bing_atc'),
      begin_checkout: config.bool('bing_bc'),
      purchase: config.bool('bing_purchase'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// FACEBOOK (META)
// ═══════════════════════════════════════════════════════════════

function buildFacebookProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('meta_status') || config.bool('meta_status', true),
    all_forms: config.bool('meta_all_forms'),
    primary: {
      status: config.bool('meta_status'),
      id: config.str('meta_id'),
      product_id_format: (config.str('meta_id_format') || productIdFormat) as ProductIdFormat,
      events: {
        view_content: config.bool('meta_vc'),
        view_collection: config.has('meta_vil') ? config.bool('meta_vil') : true,
        search: config.bool('meta_search'),
        add_to_cart: config.bool('meta_atc'),
        add_to_wishlist: config.bool('meta_atw'),
        view_cart: config.has('meta_vct') ? config.bool('meta_vct') : true,
        initiate_checkout: config.bool('meta_ic'),
        add_payment_info: config.bool('meta_api'),
        generate_lead: config.bool('meta_all_forms'),
        purchase: config.bool('meta_purchase'),
      },
    },
    secondary: {
      status: config.bool('meta_status', true),
      id: config.str('meta_id', true),
      product_id_format: (config.str('meta_id_format', true) || productIdFormat) as ProductIdFormat,
      events: {
        view_content: config.bool('meta_vc', true),
        view_collection: config.has('meta_vil', true) ? config.bool('meta_vil', true) : true,
        search: config.bool('meta_search', true),
        add_to_cart: config.bool('meta_atc', true),
        add_to_wishlist: config.bool('meta_atw', true),
        view_cart: config.has('meta_vct', true) ? config.bool('meta_vct', true) : true,
        initiate_checkout: config.bool('meta_ic', true),
        add_payment_info: config.bool('meta_api', true),
        generate_lead: config.bool('meta_all_forms', true),
        purchase: config.bool('meta_purchase', true),
      },
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// SERVER-SIDE
// ═══════════════════════════════════════════════════════════════

function buildServerSideProperties(config: HeadlessConfig) {
  // Derive azfy_attribution from cart_atr flags (matches Liquid logic)
  const cartAtrUpsert = config.has('cart_atr_upsert') ? config.bool('cart_atr_upsert') : true;
  const cartAtrAdmin = config.has('cart_atr_admin') ? config.bool('cart_atr_admin') : true;
  const azfyAttribution = !cartAtrUpsert ? false : !cartAtrAdmin ? true : 'dual';

  return {
    store_id: config.str('server_side_store_id'),
    status: config.bool('server_side_status'),
    measurement_id: config.str('ss_measurement_id') || config.str('ga4_id'),
    datalayer: config.bool('server_side_datalayer'),
    endpoint: 'https://analyzifycdn.com/collect',
    testEndpoint: 'https://stag.analyzifycdn.com/collect',
    azfy_attribution: azfyAttribution,
    shop_domain: '', // Set after buildAnalyzifyObject with shopOptions
    all_forms: config.bool('server_side_all_forms'),
    sendCartUpdateDebug: false,
    sendUpsertFailDebug: true,
    events: {
      add_to_cart: config.bool('server_side_add_to_cart'),
      view_item: config.has('server_side_view_item') ? config.bool('server_side_view_item') : true,
      view_item_list: true,
      page_view: config.has('server_side_page_view') ? config.bool('server_side_page_view') : true,
      begin_checkout: config.bool('server_side_begin_checkout'),
      search: config.has('server_side_search') ? config.bool('server_side_search') : true,
      generate_lead: config.bool('server_side_all_forms'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// PINTEREST
// ═══════════════════════════════════════════════════════════════

function buildPinterestProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('pinterest_status'),
    id: config.str('pinterest_id'),
    product_id_format: (config.str('pinterest_id_format') || 'product_sku') as ProductIdFormat,
    events: {
      view_item_list: config.bool('pinterest_vil'),
      view_item: config.bool('pinterest_vi'),
      add_to_cart: config.bool('pinterest_atc'),
      purchase: config.bool('pinterest_purchase'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// CRITEO
// ═══════════════════════════════════════════════════════════════

function buildCriteoProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('criteo_status'),
    id: config.str('criteo_id'),
    product_id_format: (config.str('criteo_id_format') || 'product_sku') as ProductIdFormat,
    events: {
      view_item_list: config.bool('criteo_vil'),
      view_item: config.bool('criteo_vi'),
      add_to_cart: config.bool('criteo_atc'),
      view_cart: config.bool('criteo_vc'),
      purchase: config.bool('criteo_purchase'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// CLARITY
// ═══════════════════════════════════════════════════════════════

function buildClarityProperties(config: HeadlessConfig) {
  return {
    status: config.bool('clarity_status'),
    id: config.str('clarity_id'),
    events: {
      view_item: config.bool('clarity_view_item'),
      view_item_list: config.bool('clarity_view_item_list'),
      view_cart: config.bool('clarity_view_cart'),
      add_to_cart: config.bool('clarity_add_to_cart'),
      add_to_wishlist: config.bool('clarity_add_to_wishlist'),
      remove_from_cart: config.bool('clarity_remove_from_cart'),
      begin_checkout: config.bool('clarity_begin_checkout'),
      search: config.bool('clarity_search'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// HOTJAR
// ═══════════════════════════════════════════════════════════════

function buildHotjarProperties(config: HeadlessConfig) {
  return {
    status: config.bool('hotjar_status'),
    id: config.str('hotjar_id'),
    events: {
      view_item: config.bool('hotjar_view_item'),
      view_item_list: config.bool('hotjar_view_item_list'),
      view_cart: config.bool('hotjar_view_cart'),
      add_to_cart: config.bool('hotjar_add_to_cart'),
      add_to_wishlist: config.bool('hotjar_add_to_wishlist'),
      remove_from_cart: config.bool('hotjar_remove_from_cart'),
      begin_checkout: config.bool('hotjar_begin_checkout'),
      search: config.bool('hotjar_search'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// SNAPCHAT
// ═══════════════════════════════════════════════════════════════

function buildSnapchatProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('snapchat_status'),
    id: config.str('snapchat_id'),
    product_id_format: (config.str('snapchat_id_format') || 'product_sku') as ProductIdFormat,
    events: {
      view_item: config.bool('snapchat_vc'),
      add_to_cart: config.bool('snapchat_atc'),
      initiate_checkout: config.bool('snapchat_bc'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// KLAVIYO
// ═══════════════════════════════════════════════════════════════

function buildKlaviyoProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('klaviyo_status'),
    id: config.str('klaviyo_id'),
    product_id_format: (config.str('klaviyo_id_format') || 'product_sku') as ProductIdFormat,
    events: {
      subscribe: config.bool('klaviyo_subscribe'),
      view_item_list: config.bool('klaviyo_vil'),
      view_item: config.bool('klaviyo_vi'),
      add_to_cart: config.bool('klaviyo_atc'),
      add_to_wishlist: config.has('klaviyo_atw') ? config.bool('klaviyo_atw') : true, // Default true
      begin_checkout: config.bool('klaviyo_bc'),
      search: config.bool('klaviyo_search'),
      purchase: config.bool('klaviyo_purchase'),
    },
  };
}

// ═══════════════════════════════════════════════════════════════
// TIKTOK
// ═══════════════════════════════════════════════════════════════

function buildTiktokProperties(config: HeadlessConfig) {
  const productIdFormat = (config.str('product_id_format') || 'product_id') as ProductIdFormat;

  return {
    status: config.bool('tiktok_status'),
    id: config.str('tiktok_id'),
    product_id_format: (config.str('tiktok_id_format') || 'product_sku') as ProductIdFormat,
    all_forms: config.bool('tiktok_all_forms'),
    events: {
      view_item: config.bool('tiktok_vi'),
      add_to_cart: config.bool('tiktok_atc'),
      begin_checkout: config.bool('tiktok_bc'),
      search: config.bool('tiktok_search'),
      purchase: config.bool('tiktok_purchase'),
      generate_lead: config.bool('tiktok_all_forms'),
    },
  };
}
